export const HORA_INICIO_AGENDA = 9;
export const HORA_FIN_AGENDA = 19;
export const ALTO_HORA_AGENDA_PX = 60;
export const SNAP_MINUTOS_AGENDA = 15;
/** Mínimo al redimensionar / hueco vacío: 5 min (1/12 de hora en la rejilla). */
export const DURACION_MINIMA_CITA_MS = 5 * 60 * 1000;

export function pad(n) {
  return String(n).padStart(2, "0");
}

export function fechaISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD en hora local (evita desfases al colocar citas en el calendario). */
export function fechaLocalDeIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return fechaISO(d);
}

export function duracionCitaMs(cita) {
  const ini = new Date(cita.fecha_inicio);
  const fin = new Date(cita.fecha_fin || cita.fecha_inicio);
  const d = fin.getTime() - ini.getTime();
  return d > 60000 ? d : 3600000;
}

export function calcularFechasCitaDesdeDrop(diaDate, clientY, columnEl, duracionMs) {
  const rect = columnEl.getBoundingClientRect();
  const y = Math.max(0, Math.min(clientY - rect.top, rect.height - 1));
  const minutosTotales = (y / ALTO_HORA_AGENDA_PX) * 60 + HORA_INICIO_AGENDA * 60;
  const snapped = Math.round(minutosTotales / SNAP_MINUTOS_AGENDA) * SNAP_MINUTOS_AGENDA;
  const h = Math.max(HORA_INICIO_AGENDA, Math.min(HORA_FIN_AGENDA, Math.floor(snapped / 60)));
  const m = snapped % 60;
  const diaStr = fechaISO(diaDate);
  const nuevaIni = new Date(`${diaStr}T${pad(h)}:${pad(m)}:00`);
  const nuevaFin = new Date(nuevaIni.getTime() + duracionMs);
  const maxFin = new Date(`${diaStr}T${pad(HORA_FIN_AGENDA)}:00:00`);
  if (nuevaFin > maxFin) {
    const ajustadaIni = new Date(maxFin.getTime() - duracionMs);
    const minIni = new Date(`${diaStr}T${pad(HORA_INICIO_AGENDA)}:00:00`);
    const iniFinal = ajustadaIni < minIni ? minIni : ajustadaIni;
    return {
      fecha_inicio: iniFinal.toISOString(),
      fecha_fin: new Date(iniFinal.getTime() + duracionMs).toISOString(),
    };
  }
  return { fecha_inicio: nuevaIni.toISOString(), fecha_fin: nuevaFin.toISOString() };
}

export function calcularFechaFinDesdeResize(diaDate, clientY, columnEl, fechaInicioIso) {
  const rect = columnEl.getBoundingClientRect();
  const y = Math.max(0, Math.min(clientY - rect.top, rect.height - 1));
  const minutosTotales = (y / ALTO_HORA_AGENDA_PX) * 60 + HORA_INICIO_AGENDA * 60;
  const snapped = Math.round(minutosTotales / SNAP_MINUTOS_AGENDA) * SNAP_MINUTOS_AGENDA;
  const h = Math.max(HORA_INICIO_AGENDA, Math.min(HORA_FIN_AGENDA, Math.floor(snapped / 60)));
  const m = snapped % 60;
  const diaStr = fechaISO(diaDate);
  const ini = new Date(fechaInicioIso);
  let nuevaFin = new Date(`${diaStr}T${pad(h)}:${pad(m)}:00`);
  const minFin = new Date(ini.getTime() + DURACION_MINIMA_CITA_MS);
  if (nuevaFin.getTime() < minFin.getTime()) nuevaFin = minFin;
  const maxFin = new Date(`${diaStr}T${pad(HORA_FIN_AGENDA)}:00:00`);
  if (nuevaFin > maxFin) nuevaFin = maxFin;
  return nuevaFin.toISOString();
}

export function posicionBloqueCita(cita, altoHora = ALTO_HORA_AGENDA_PX) {
  const ini = new Date(cita.fecha_inicio);
  const fin = new Date(cita.fecha_fin || cita.fecha_inicio);
  const hIni = ini.getHours() + ini.getMinutes() / 60;
  // Escala real: 5 min = 1/12 de hora (como Google Calendar). Sin mínimo de 56px.
  let durHoras = (fin.getTime() - ini.getTime()) / 3600000;
  if (!(durHoras > 0)) durHoras = 5 / 60;
  const topPx = (hIni - HORA_INICIO_AGENDA) * altoHora;
  const altoPx = Math.max(durHoras * altoHora, altoHora / 12);
  return { topPx, altoPx };
}

/**
 * Reparte citas solapadas del mismo día en columnas para que se vean todas
 * (p. ej. captación 10–11 y visita de venta 10:30).
 * @returns {Map<string, { column: number, columnCount: number }>}
 */
export function computeAgendaOverlapLayout(citas) {
  const layout = new Map();
  if (!Array.isArray(citas) || !citas.length) return layout;

  const items = citas
    .map((cita) => {
      const start = new Date(cita.fecha_inicio).getTime();
      const endRaw = new Date(cita.fecha_fin || cita.fecha_inicio).getTime();
      const end = Math.max(endRaw, start + 60000);
      return { cita, start, end };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const groups = [];
  let current = null;
  for (const item of items) {
    if (!current || item.start >= current.maxEnd) {
      if (current) groups.push(current.items);
      current = { maxEnd: item.end, items: [item] };
    } else {
      current.items.push(item);
      current.maxEnd = Math.max(current.maxEnd, item.end);
    }
  }
  if (current) groups.push(current.items);

  for (const group of groups) {
    const columnEnds = [];
    for (const item of group) {
      let col = columnEnds.findIndex((end) => end <= item.start);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(item.end);
      } else {
        columnEnds[col] = item.end;
      }
      layout.set(item.cita.id, { column: col, columnCount: columnEnds.length });
    }
    const maxCols = columnEnds.length;
    for (const item of group) {
      const slot = layout.get(item.cita.id);
      if (slot) slot.columnCount = maxCols;
    }
  }

  return layout;
}
